import moduleInfo from '../module.json';
import type { AppRuntime } from '@ayocore/platform/client/runtime/AppRuntime';
import { createElement, lazy } from 'react';
import {
  IconTruck,
  IconCoin,
  IconClipboardList,
  IconReceipt,
  IconEye,
  IconShoppingCart,
} from '@tabler/icons-react';
import { DealViewExtensions } from '@ayocore/erp/crm/client/components/Deals/views/DealViewExtensions';
import { SUPPLIER_INVOICES_REGISTER_URL } from '@ayocore/erp/procurement/constants';
import { registerProcurementNotificationScopes } from './notification-scopes';
import { SupplierInvoicesMenuBadge } from './components/SupplierInvoices/SupplierInvoicesMenuBadge';

const DealSupplierInvoicesBlock = lazy(() =>
  import('./components/SupplierInvoices/DealSupplierInvoicesBlock').then(m => ({
    default: m.DealSupplierInvoicesBlock,
  }))
);

/**
 * Регистрация клиентской части модуля закупок (procurement).
 * Сорсинг, реестр платежей и вкладка «Закупки» в карточке сделки (через слот CRM).
 */
export async function setup(app: AppRuntime): Promise<void> {
  const procurement = app.useModule(moduleInfo.name, {
    version: moduleInfo.version,
    description: moduleInfo.description,
    navigationOrder: 15,
  });

  registerProcurementNotificationScopes();

  // Вкладка «Закупки» (счета поставщиков) в карточке сделки — слот DealView.
  DealViewExtensions.tabs.register(({ deal }) => ({
    value: 'invoices',
    label: 'Закупки',
    content: createElement(DealSupplierInvoicesBlock, { dealId: Number(deal.id) }),
  }));

  const groupProcurement = procurement.createNavigationGroup({
    key: 'procurement',
    label: 'Закупки',
    icon: IconTruck,
    order: 1,
  });

  procurement.useNavigation([
    {
      group: groupProcurement,
      icon: IconCoin,
      label: 'Сорсинг',
      url: '/procurement/sourcing',
      component: lazy(() => import('./components/Sourcing/SourcingBoardPage')),
    },
    {
      group: groupProcurement,
      icon: IconClipboardList,
      label: 'Заявки поставщикам',
      url: '/procurement/requests',
      component: lazy(() => import('./components/SupplierRequests/SupplierRequestsPage')),
    },
    {
      group: groupProcurement,
      icon: IconShoppingCart,
      label: 'Закупки',
      url: '/procurement/purchase-orders',
      component: lazy(() => import('./components/PurchaseOrders/PurchaseOrdersList')),
    },
    {
      group: groupProcurement,
      icon: IconTruck,
      label: 'Поставщики',
      url: '/procurement/suppliers',
      component: lazy(() => import('./components/Suppliers/SuppliersList')),
    },
    {
      group: groupProcurement,
      icon: IconReceipt,
      label: 'Реестр платежей',
      url: SUPPLIER_INVOICES_REGISTER_URL,
      component: lazy(() => import('./components/SupplierInvoices/SupplierInvoicesList')),
      badge: SupplierInvoicesMenuBadge,
    },
    {
      icon: IconEye,
      label: 'Просмотр счёта поставщика',
      url: `${SUPPLIER_INVOICES_REGISTER_URL}/:id`,
      component: lazy(() =>
        import('./components/SupplierInvoices/SupplierInvoiceViewPage').then(m => ({
          default: m.SupplierInvoiceViewPage,
        }))
      ),
      hiddenFromMenu: true,
    },
    {
      icon: IconEye,
      label: 'Просмотр поставщика',
      url: '/procurement/suppliers/:id',
      component: lazy(() =>
        import('./components/Suppliers/views/SupplierViewPage').then(m => ({
          default: m.SupplierViewPage,
        }))
      ),
      hiddenFromMenu: true,
    },
  ]);
}
